import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Placeholder, Row, Col, Container, Table, Button, Badge } from 'react-bootstrap';
import { FaUsers, FaCarSide, FaClipboardList, FaUserTie, FaUserFriends } from 'react-icons/fa';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  PointElement,
  LineElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  PointElement,
  LineElement
);

// Injected once — powers the .skeleton-shimmer blocks used by ChartSkeleton
const shimmerStyleTag = (
  <style>{`
    .skeleton-shimmer {
      background: linear-gradient(90deg, #e9ecef 25%, #f3f4f6 37%, #e9ecef 63%);
      background-size: 400% 100%;
      animation: skeleton-shimmer 1.4s ease infinite;
    }
    @keyframes skeleton-shimmer {
      0% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `}</style>
);

const ChartSkeleton = ({ circular }) => (
  <div
    style={{
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      className="skeleton-shimmer"
      style={
        circular
          ? { width: '220px', height: '220px', borderRadius: '50%' }
          : { width: '100%', height: '85%', borderRadius: '16px' }
      }
    />
  </div>
);

const Dashboard = () => {
  const [userCount, setUserCount] = useState(0);
  const [vehicleCount, setVehicleCount] = useState(0);
  const [staffCount, setStaffCount] = useState(0);
  const [ownerCount, setOwnerCount] = useState(0);
  const [bookingStats, setBookingStats] = useState({
    pending: 0,
    confirmed: 0,
    cancelled: 0,
    completed: 0,
    active: 0,
  });
  const [paymentStats, setPaymentStats] = useState({ paid: 0, pending: 0 });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const bookingsPerPage = 5;
  const [vehicleTypes, setVehicleTypes] = useState({});
  const [totalBookingsCount, setTotalBookingsCount] = useState(0);
  const navigate = useNavigate();

  // Tracks whether each section's own API call has resolved (success or fail) —
  // drives per-card / per-chart skeletons instead of one big spinner.
  const [loaded, setLoaded] = useState({
    users: false,
    vehicles: false,
    staff: false,
    owners: false,
    bookings: false,
  });

  useEffect(() => {
    // 🔐 Get token (adjust key name if yours is different)
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('adminToken');

    const authHeader = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};

    // Each request now has its own timeout and updates state the moment IT resolves —
    // it no longer waits for the other 4 requests to finish first (that was the loading bottleneck).
    // allusers is known to be slower on the backend, so it gets a longer timeout instead of
    // getting cancelled or blocking the fast endpoints.
    const FAST_TIMEOUT = 10000;
    const USERS_TIMEOUT = 20000;

    // 👥 Users
    axios
      .get('https://varahibackend.varahiselfdrivecars.com/api/admin/allusers?page=1&limit=10', {
        ...authHeader,
        timeout: USERS_TIMEOUT,
      })
      .then((userRes) => {
        console.log('👥 Users API:', userRes.data);
        setUserCount(userRes.data?.pagination?.totalUsers || 0);
      })
      .catch((err) => console.error('❌ Users fetch error:', err.response?.data || err.message))
      .finally(() => setLoaded((prev) => ({ ...prev, users: true })));

    // 🚗 Vehicles
    axios
      .get('https://varahibackend.varahiselfdrivecars.com/api/car/get-cars', { timeout: FAST_TIMEOUT })
      .then((vehicleRes) => {
        console.log('🚗 Vehicles API:', vehicleRes.data);
        const totalVehicles =
          vehicleRes.data?.total ||
          vehicleRes.data?.count ||
          vehicleRes.data?.cars?.length ||
          vehicleRes.data?.data?.length ||
          (Array.isArray(vehicleRes.data) ? vehicleRes.data.length : 0);
        setVehicleCount(totalVehicles);

        const vehicles =
          vehicleRes.data?.cars ||
          vehicleRes.data?.data ||
          (Array.isArray(vehicleRes.data) ? vehicleRes.data : []);
        const vehicleTypeCounts = {};
        vehicles.forEach((car) => {
          const type = car.type || 'Unknown';
          vehicleTypeCounts[type] = (vehicleTypeCounts[type] || 0) + 1;
        });
        setVehicleTypes(vehicleTypeCounts);
      })
      .catch((err) => console.error('❌ Vehicles fetch error:', err.response?.data || err.message))
      .finally(() => setLoaded((prev) => ({ ...prev, vehicles: true })));

    // 👔 Staff
    axios
      .get('https://varahibackend.varahiselfdrivecars.com/api/admin/getallstaffs', {
        ...authHeader,
        timeout: FAST_TIMEOUT,
      })
      .then((staffRes) => {
        console.log('👔 Staff API:', staffRes.data);
        const totalStaff =
          staffRes.data?.pagination?.totalStaff ||
          staffRes.data?.totalStaff ||
          staffRes.data?.total ||
          staffRes.data?.count ||
          staffRes.data?.staff?.length ||
          staffRes.data?.data?.length ||
          (Array.isArray(staffRes.data) ? staffRes.data.length : 0);
        setStaffCount(totalStaff);
      })
      .catch((err) => console.error('❌ Staff fetch error:', err.response?.data || err.message))
      .finally(() => setLoaded((prev) => ({ ...prev, staff: true })));

    // 🧑‍💼 Owners
    axios
      .get('https://varahibackend.varahiselfdrivecars.com/api/admin/allowners', {
        ...authHeader,
        timeout: FAST_TIMEOUT,
      })
      .then((ownerRes) => {
        console.log('🧑‍💼 Owners API:', ownerRes.data);
        const totalOwners =
          ownerRes.data?.pagination?.totalOwners ||
          ownerRes.data?.count ||
          ownerRes.data?.total ||
          ownerRes.data?.totalOwners ||
          ownerRes.data?.data?.length ||
          ownerRes.data?.owners?.length ||
          (Array.isArray(ownerRes.data) ? ownerRes.data.length : 0);
        setOwnerCount(totalOwners);
      })
      .catch((err) => console.error('❌ Owners fetch error:', err.response?.data || err.message))
      .finally(() => setLoaded((prev) => ({ ...prev, owners: true })));

    // 📋 Bookings — this one also drives the charts + recent bookings table
    axios
      .get('https://varahibackend.varahiselfdrivecars.com/api/staff/allbookingsforadmin', {
        ...authHeader,
        timeout: FAST_TIMEOUT,
      })
      .then((bookingRes) => {
        console.log('📋 Bookings API:', bookingRes.data);
        const totalBookings =
          bookingRes.data?.pagination?.totalBookings ||
          bookingRes.data?.totalBookings ||
          bookingRes.data?.total ||
          bookingRes.data?.count ||
          bookingRes.data?.bookings?.length ||
          (Array.isArray(bookingRes.data) ? bookingRes.data.length : 0);
        setTotalBookingsCount(totalBookings);

        const bookings =
          bookingRes.data?.bookings ||
          bookingRes.data?.data ||
          (Array.isArray(bookingRes.data) ? bookingRes.data : []);

        const statusCounts = {
          pending: 0,
          confirmed: 0,
          cancelled: 0,
          completed: 0,
          active: 0,
        };
        const paymentCounts = { paid: 0, pending: 0 };

        bookings.forEach((booking) => {
          const status = booking.status?.toLowerCase();
          if (status && statusCounts.hasOwnProperty(status)) statusCounts[status]++;
          const paymentStatus = booking.paymentStatus?.toLowerCase();
          if (paymentStatus === 'paid') paymentCounts.paid++;
          else if (paymentStatus === 'pending') paymentCounts.pending++;
        });

        setBookingStats(statusCounts);
        setPaymentStats(paymentCounts);
        setRecentBookings(bookings.slice(0, 10));
      })
      .catch((err) => console.error('❌ Bookings fetch error:', err.response?.data || err.message))
      .finally(() => {
        setLoading(false); // bookings drives the table/charts below, so loading ends once it settles either way
        setLoaded((prev) => ({ ...prev, bookings: true }));
      });
  }, []);

  const bookingCount = totalBookingsCount;

  const indexOfLastBooking = currentPage * bookingsPerPage;
  const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
  const currentBookings = recentBookings.slice(indexOfFirstBooking, indexOfLastBooking);
  const totalPages = Math.ceil(recentBookings.length / bookingsPerPage);

  const handlePrevPage = () => currentPage > 1 && setCurrentPage((prev) => prev - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage((prev) => prev + 1);

  const barData = {
    labels: ['Users', 'Staff', 'Owners', 'Vehicles', 'Bookings'],
    datasets: [
      {
        label: 'Count',
        data: [userCount, staffCount, ownerCount, vehicleCount, bookingCount],
        backgroundColor: ['#a855f7', '#10b981', '#f97316', '#6366f1', '#f59e0b'],
        borderRadius: 12,
      },
    ],
  };

  const pieData = {
    labels: ['Paid', 'Pending'],
    datasets: [
      {
        data: [paymentStats.paid, paymentStats.pending],
        backgroundColor: ['#22c55e', '#eab308'],
        borderWidth: 0,
      },
    ],
  };

  const statusPieData = {
    labels: ['Pending', 'Confirmed', 'Active', 'Completed', 'Cancelled'],
    datasets: [
      {
        data: [
          bookingStats.pending,
          bookingStats.confirmed,
          bookingStats.active,
          bookingStats.completed,
          bookingStats.cancelled,
        ],
        backgroundColor: ['#facc15', '#14b8a6', '#3b82f6', '#22c55e', '#ef4444'],
        borderWidth: 0,
      },
    ],
  };

  const lineData = {
    labels: recentBookings.map((_, i) => `#${i + 1}`),
    datasets: [
      {
        label: 'Total Price (₹)',
        data: recentBookings.map((b) => b.totalPrice || 0),
        fill: true,
        borderColor: '#a855f7',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        tension: 0.4,
        pointBackgroundColor: '#a855f7',
        pointBorderColor: '#fff',
        pointRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 12, padding: 15, font: { size: 12 }, color: '#334155' },
      },
      tooltip: { backgroundColor: '#1e293b', titleColor: '#f1f5f9', bodyColor: '#cbd5e1' },
    },
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'active': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getPaymentBadge = (paymentStatus) => {
    switch (paymentStatus?.toLowerCase()) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      default: return 'secondary';
    }
  };

  const cardConfig = [
    { key: 'users', label: 'Total Users', value: userCount, icon: <FaUsers />, path: '/admin/users', bg: 'linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%)', iconBg: '#a855f7' },
    { key: 'staff', label: 'Total Staff', value: staffCount, icon: <FaUserTie />, path: '/admin/staff', bg: 'linear-gradient(135deg, #e0f2fe 0%, #ecfdf5 100%)', iconBg: '#10b981' },
    { key: 'owners', label: 'Total Owners', value: ownerCount, icon: <FaUserFriends />, path: '/admin/owners', bg: 'linear-gradient(135deg, #ffede0 0%, #fff5e6 100%)', iconBg: '#f97316' },
    { key: 'vehicles', label: 'Total Vehicles', value: vehicleCount, icon: <FaCarSide />, path: '/admin/vehicles', bg: 'linear-gradient(135deg, #e0e7ff 0%, #eef2ff 100%)', iconBg: '#6366f1' },
    { key: 'bookings', label: 'Total Bookings', value: bookingCount, icon: <FaClipboardList />, path: '/admin/bookings', bg: 'linear-gradient(135deg, #ffedd5 0%, #fff7ed 100%)', iconBg: '#f59e0b' },
  ];

  return (
    <div style={styles.root}>
      {shimmerStyleTag}
      <Container fluid className="px-4 py-4">
        <h2 className="mb-4 fw-bold text-center" style={styles.heading}>
          ✨ Admin Dashboard ✨
        </h2>

        <Row className="g-4 mb-5">
          {cardConfig.map((card, idx) => (
            <Col md={6} lg={4} key={idx} className="d-flex">
              <div
                style={{ ...styles.glassCard, background: card.bg, cursor: 'pointer', width: '100%' }}
                onClick={() => navigate(card.path)}
              >
                <div className="d-flex align-items-center">
                  <div style={{ ...styles.iconWrapper, background: `${card.iconBg}20` }}>
                    <span style={{ color: card.iconBg, fontSize: '1.8rem' }}>{card.icon}</span>
                  </div>
                  <div className="ms-3" style={{ minWidth: '80px' }}>
                    <h6 style={styles.cardLabel}>{card.label}</h6>
                    {loaded[card.key] ? (
                      <h3 style={styles.cardValue}>{card.value.toLocaleString()}</h3>
                    ) : (
                      <Placeholder as="h3" animation="glow" className="mb-0">
                        <Placeholder xs={7} style={{ height: '1.6rem', borderRadius: '6px' }} />
                      </Placeholder>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <Row className="g-4 mb-4">
          <Col xl={6}>
            <div style={styles.chartCard}>
              <h5 className="fw-semibold mb-3" style={styles.chartTitle}>📊 Summary Overview</h5>
              <div style={{ height: '300px' }}>
                {!loaded.bookings ? (
                  <ChartSkeleton />
                ) : (
                  <Bar data={barData} options={{ ...chartOptions, plugins: { legend: { display: false } } }} />
                )}
              </div>
            </div>
          </Col>
          <Col xl={6}>
            <div style={styles.chartCard}>
              <h5 className="fw-semibold mb-3" style={styles.chartTitle}>📌 Booking Status</h5>
              <div style={{ height: '300px' }}>
                {!loaded.bookings ? <ChartSkeleton circular /> : <Pie data={statusPieData} options={chartOptions} />}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          <Col xl={6}>
            <div style={styles.chartCard}>
              <h5 className="fw-semibold mb-3" style={styles.chartTitle}>📈 Booking Price Trend</h5>
              <div style={{ height: '300px' }}>
                {!loaded.bookings ? <ChartSkeleton /> : <Line data={lineData} options={chartOptions} />}
              </div>
            </div>
          </Col>
          <Col xl={6}>
            <div style={styles.chartCard}>
              <h5 className="fw-semibold mb-3" style={styles.chartTitle}>💰 Payment Status</h5>
              <div style={{ height: '300px' }}>
                {!loaded.bookings ? <ChartSkeleton circular /> : <Doughnut data={pieData} options={chartOptions} />}
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div style={styles.chartCard}>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <h5 className="fw-semibold mb-0" style={styles.chartTitle}>🕒 Recent Bookings</h5>
                <Button variant="outline-primary" size="sm" onClick={() => navigate('/admin/bookings')} style={styles.viewAllBtn}>
                  View All →
                </Button>
              </div>

              <div className="table-responsive">
                <Table hover className="dashboard-table" style={styles.table}>
                  <thead style={styles.tableHead}>
                    <tr>
                      <th>ID</th><th>User</th><th>Vehicle</th><th>Dates</th><th>Location</th><th>Price</th><th>Status</th><th>Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loaded.bookings ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={`skeleton-${i}`} style={styles.tableRow}>
                          {Array.from({ length: 8 }).map((__, j) => (
                            <td key={j}>
                              <Placeholder as="span" animation="glow">
                                <Placeholder xs={j === 3 ? 10 : 6} style={{ height: '0.9rem', borderRadius: '4px' }} />
                              </Placeholder>
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      currentBookings.map((booking) => (
                        <tr key={booking._id} style={styles.tableRow}>
                          <td className="text-muted">{booking._id?.slice(-6)}</td>
                          <td>{booking.userId?.name || 'N/A'}</td>
                          <td>{booking.car?.carName || 'N/A'}</td>
                          <td><small>{new Date(booking.rentalStartDate).toLocaleDateString()} – {new Date(booking.rentalEndDate).toLocaleDateString()}</small></td>
                          <td>{booking.pickupLocation || 'N/A'}</td>
                          <td className="text-success fw-bold">₹{booking.totalPrice}</td>
                          <td><Badge bg={getStatusBadge(booking.status)} className="text-capitalize px-2 py-1">{booking.status || 'N/A'}</Badge></td>
                          <td><Badge bg={getPaymentBadge(booking.paymentStatus)} className="text-capitalize px-2 py-1">{booking.paymentStatus || 'N/A'}</Badge></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {loaded.bookings && totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3 pt-2">
                  <Button variant="outline-secondary" onClick={handlePrevPage} disabled={currentPage === 1} style={styles.paginationBtn}>Previous</Button>
                  <span style={{ color: '#475569' }}>Page {currentPage} of {totalPages}</span>
                  <Button variant="outline-secondary" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0} style={styles.paginationBtn}>Next</Button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const styles = {
  root: {
    background: '#ffffff',
    minHeight: '100vh',
    fontFamily: "'Poppins', 'Segoe UI', sans-serif",
  },
  heading: {
    background: 'linear-gradient(135deg, #a855f7, #6366f1)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
    fontSize: '2rem',
  },
  glassCard: {
    borderRadius: '28px',
    border: '1px solid rgba(168, 85, 247, 0.15)',
    padding: '1.25rem 1.5rem',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
  },
  iconWrapper: {
    width: '54px',
    height: '54px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '20px',
  },
  cardLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#64748b',
    marginBottom: '4px',
  },
  cardValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
    lineHeight: 1.2,
  },
  chartCard: {
    background: '#ffffff',
    borderRadius: '28px',
    border: '1px solid rgba(168, 85, 247, 0.2)',
    padding: '1.25rem 1.5rem',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
  },
  chartTitle: {
    color: '#334155',
    borderLeft: '3px solid #a855f7',
    paddingLeft: '12px',
    marginBottom: '1.2rem',
  },
  table: {
    color: '#1e293b',
    borderCollapse: 'separate',
    borderSpacing: '0 8px',
  },
  tableHead: {
    background: '#f1f5f9',
    borderRadius: '16px',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#475569',
  },
  tableRow: {
    background: '#ffffff',
    borderRadius: '16px',
    transition: 'all 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  viewAllBtn: {
    borderColor: '#a855f7',
    color: '#a855f7',
    borderRadius: '40px',
    padding: '0.3rem 1rem',
    fontSize: '0.8rem',
  },
  paginationBtn: {
    borderColor: '#cbd5e1',
    color: '#475569',
    borderRadius: '40px',
    padding: '0.3rem 1rem',
    fontSize: '0.8rem',
  },
};

export default Dashboard;